import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import AnswerCard from '../answers/AnswerCard';
import EmptyAnswer from '../answers/EmptyAnswer';
import UnsharedAnswer from '../answers/UnsharedAnswer';
import Question from '../questions/Question';
import { QuillInput } from '@treams/treams-ui';
import Delta from 'quill-delta';
import IrrelevantAnswer from '../answers/IrrelevantAnswer';

import AnswerLine from '../answers/AnswerLine';
import { useDebouncedCallback } from 'use-debounce';

import useProtectedValue from 'conversations/conversation/hooks/useProtectedValue';
import SaveLine from '../layout/SaveLine';
import useDetectPrint from 'conversations/evaluation-submission/hooks/useDetectPrint';
import Comments from '../comments/Comments';

function OpenQuestionAnswerInput({
	item,
	answer,
	participant,
}: {
	item: graphql.OpenQuestionItem;
	answer: graphql.OpenQuestionItemAnswer;
	participant: graphql.Participant;
}) {
	const [{ value, dirty, changed }, { setValue, setDirty, reset }] = useProtectedValue(
		new Delta(answer.content),
	);

	const autosave = answer.status === graphql.ConversationItemAnswerStatus.Draft;

	const [answerOpenQuestionItemMutation, { loading }] = graphql.useAnswerOpenQuestionItemMutation();

	const saveChange = async (value: Delta) => {
		const response = await answerOpenQuestionItemMutation({
			variables: {
				conversationItemID: item.conversationItemID,
				content: value,
			},
		});
		const isDirty = Boolean(
			value.diff(new Delta(response.data?.answerOpenQuestionItem?.content)).ops.length > 0,
		);

		setDirty(isDirty);
	};

	const debouncedSaveChange = useDebouncedCallback(saveChange, 400);

	const handleChange = async (value: Delta) => {
		setValue(value);
		setDirty(true);

		if (autosave) {
			debouncedSaveChange(value);
		}
	};

	return (
		<AnswerLine
			user={participant.user}
			side={participant.role === graphql.ConversationRole.Requester ? 'left' : 'right'}
		>
			<AnswerCard>
				<QuillInput debounce={false} onChange={handleChange} value={value} />
			</AnswerCard>
			{changed && (
				<SaveLine
					onSave={() => saveChange(value!)}
					changed={changed}
					saving={loading}
					dirty={dirty}
					onDiscard={reset}
					autosave={autosave}
				/>
			)}
		</AnswerLine>
	);
}

function OpenQuestionAnswer({
	answer,
	item,
}: {
	answer: graphql.OpenQuestionItemAnswer;
	item: graphql.OpenQuestionItem;
}) {
	const isPrinting = useDetectPrint();

	if (!answer.relevant) {
		return <IrrelevantAnswer participant={answer.participant} />;
	}

	if (!answer.viewerCanView) {
		return <UnsharedAnswer participant={answer.participant} />;
	}

	if (answer.viewerCanUpdate && !isPrinting) {
		return <OpenQuestionAnswerInput answer={answer} item={item} participant={answer.participant} />;
	}

	if (answer.content?.ops.length === 0) {
		return <EmptyAnswer participant={answer.participant} />;
	}

	return (
		<AnswerLine
			user={answer.participant.user}
			side={answer.participant.role === graphql.ConversationRole.Requester ? 'left' : 'right'}
		>
			<AnswerCard>
				<QuillText quill={answer.content ?? { ops: [] }} />
			</AnswerCard>
		</AnswerLine>
	);
}

export default function OpenQuestionItem(props: {
	item: graphql.OpenQuestionItem;
	participants: graphql.Participant[];
}) {
	const { localize } = useLocalize();

	return (
		<Question
			title={localize(props.item.question).text}
			number={props.item.number}
			rightSection={<Comments item={props.item} />}
		>
			{props.item.answers.map((answer: graphql.OpenQuestionItemAnswer) => (
				<OpenQuestionAnswer item={props.item} answer={answer} key={answer.id} />
			))}
		</Question>
	);
}
